/**
 * WOM Collection Ratings (TrueBrand)
 * Shows WOM star rating + review count on collection/product listing cards.
 * Handles dynamically loaded content (related products, quick view, etc.)
 */
(function() {
  'use strict';

  const STATE_KEY = '__womCollectionRatings_vastramay';
  const API_URL = 'https://api.thewordofmouth.in/api/overall_company/vastramay';

  // Prevent multiple initializations
  if (window[STATE_KEY] && window[STATE_KEY].initialized) {
    return;
  }

  window[STATE_KEY] = window[STATE_KEY] || { data: null, fetching: false, initialized: false };
  const state = window[STATE_KEY];
  state.initialized = true;

  function slugifyTitle(title) {
    if (!title) return '';
    let filtered = title.split(/\s*[-–]\s*/)[0].trim();
    filtered = filtered
      .replace(/[^\w]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase();
    return filtered;
  }

  function resolveData(element, dataset) {
    if (!dataset) return null;
    const rawHandle = (element.getAttribute('data-product-handle') || '').toLowerCase();
    const rawTitle = element.getAttribute('data-product-title') || '';
    const rawSkus = element.getAttribute('data-product-skus') || '';
    const slugFromTitle = slugifyTitle(rawTitle);

    // 1. Try SKUs first (API keys are SKU-based)
    if (rawSkus) {
      const skuList = rawSkus.split(',').map(s => s.trim()).filter(Boolean);
      let bestMatch = null;
      for (const sku of skuList) {
        const match = dataset[sku];
        if (match && Number(match.total_reviews) > 0) {
          if (!bestMatch || Number(match.total_reviews) > Number(bestMatch.total_reviews)) {
            bestMatch = match;
          }
        }
      }
      if (bestMatch) return bestMatch;

      // Try partial SKU match (base SKU without size suffix like _36, _F)
      for (const sku of skuList) {
        const baseSku = sku.replace(/_[A-Z0-9]+$/, '');
        if (baseSku !== sku) {
          for (const key of Object.keys(dataset)) {
            if (key.startsWith(baseSku) && Number(dataset[key].total_reviews) > 0) {
              if (!bestMatch || Number(dataset[key].total_reviews) > Number(bestMatch.total_reviews)) {
                bestMatch = dataset[key];
              }
            }
          }
        }
      }
      if (bestMatch) return bestMatch;
    }

    // 2. Try product handle
    if (rawHandle && dataset[rawHandle]) {
      return dataset[rawHandle];
    }

    // Try handle variants
    const handleVariants = [
      rawHandle,
      rawHandle.replace(/-/g, '_'),
      rawHandle.replace(/_/g, '-'),
    ].filter(Boolean);

    for (const variant of handleVariants) {
      if (dataset && dataset[variant]) {
        return dataset[variant];
      }
    }

    // 3. Try substring match on handle against API keys
    if (rawHandle) {
      const handleUpper = rawHandle.replace(/-/g, '').toUpperCase();
      let bestMatch = null;
      for (const key of Object.keys(dataset)) {
        const keyClean = key.replace(/[_-]/g, '').toUpperCase();
        if (keyClean.includes(handleUpper) || handleUpper.includes(keyClean)) {
          if (Number(dataset[key].total_reviews) > 0) {
            if (!bestMatch || Number(dataset[key].total_reviews) > Number(bestMatch.total_reviews)) {
              bestMatch = dataset[key];
            }
          }
        }
      }
      if (bestMatch) return bestMatch;
    }

    // 4. Try exact title match
    if (dataset[rawTitle]) {
      return dataset[rawTitle];
    }

    // 5. Try title slug variants
    const titleVariants = [
      slugFromTitle,
      slugFromTitle.replace(/_/g, '-'),
    ].filter(Boolean);

    for (const variant of titleVariants) {
      if (dataset && dataset[variant]) {
        return dataset[variant];
      }
    }

    return null;
  }

  function getStarColor() {
    try {
      const config = window.vesselsWOMConfig || window.clientWOMConfig || {};
      const colors = config.colors || {};
      return colors.starColor || colors.accent || colors.primary || '#ffa503';
    } catch (error) {
      return '#ffa503';
    }
  }

  function generateStars(rating) {
    const starColor = getStarColor();
    return `
      <svg viewBox="0 0 20 20">
        <path fill="${starColor}" stroke="${starColor}" stroke-width="0.5" d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
      </svg>
    `;
  }

  function updateElements() {
    if (!state.data) return;
    const elements = document.querySelectorAll('.wom-collection-rating');
    let foundCount = 0;
    
    elements.forEach((element) => {
      // Skip already processed elements
      if (element.getAttribute('data-wom-processed') === 'true') {
        if (element.style.display === 'flex') foundCount++;
        return;
      }

      const data = resolveData(element, state.data);
      if (data && Number(data.overall_rating) > 0 && Number(data.total_reviews) > 0) {
        const ratingEl = element.querySelector('.wom-rating-number');
        const starsEl = element.querySelector('.wom-rating-stars');
        const countEl = element.querySelector('.wom-rating-count');
        
        if (ratingEl) ratingEl.textContent = Number(data.overall_rating).toFixed(1);
        if (starsEl) starsEl.innerHTML = generateStars(Number(data.overall_rating));
        if (countEl) countEl.textContent = `${data.total_reviews} Reviews`;
        element.style.display = 'flex';
        element.setAttribute('data-wom-processed', 'true');
        foundCount++;
      } else {
        element.style.display = 'none';
        element.setAttribute('data-wom-processed', 'true');
      }
    });
    
  }

  function fetchData(force) {
    if (state.fetching) return;
    if (!force && state.data) {
      updateElements();
      return;
    }

    state.fetching = true;
    fetch(API_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        state.data = data || {};
        state.fetching = false;
        updateElements();
      })
      .catch(() => {
        state.fetching = false;
      });
  }

  // Initial fetch
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => fetchData(false));
  } else {
    fetchData(false);
  }

  // Listen for custom events
  document.addEventListener('collection-updated', () => {
    state.data = null;
    fetchData(true);
  });

  document.addEventListener('wom-collection-refresh', updateElements);

  // MutationObserver for dynamically loaded products
  const observer = new MutationObserver((mutations) => {
    let hasNewRatings = false;
    
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) {
          if (node.classList && node.classList.contains('wom-collection-rating')) {
            hasNewRatings = true;
            break;
          }
          if (node.querySelectorAll) {
            const ratings = node.querySelectorAll('.wom-collection-rating');
            if (ratings.length > 0) {
              hasNewRatings = true;
              break;
            }
          }
        }
      }
      if (hasNewRatings) break;
    }
    
    if (hasNewRatings) {
      setTimeout(() => {
        if (state.data) {
          updateElements();
        } else {
          fetchData(false);
        }
      }, 50);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Fallback: Check for unprocessed elements every 2 seconds
  setInterval(() => {
    const unprocessed = document.querySelectorAll('.wom-collection-rating:not([data-wom-processed])');
    if (unprocessed.length > 0 && state.data) {
      updateElements();
    }
  }, 2000);

  // Expose global function for manual refresh
  window.womRefreshRatings = function() {
    document.querySelectorAll('.wom-collection-rating').forEach(el => {
      el.removeAttribute('data-wom-processed');
    });
    updateElements();
  };

})();
